import type { ReactNode } from 'react'
import type { SessionInfo } from '@/lib/market/types'

import { useRouter } from 'next/router'
import { RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLive } from '@/components/market/live-provider'
import { useNow } from '@/hooks/use-mounted'
import { formatEatClock, formatNextOpen, phaseLabel } from '@/lib/market/calendar'

interface MarketStatusProps {
    session: SessionInfo | null
    compact?: boolean
}

const MarketStatus = ({ session, compact = false }: MarketStatusProps): ReactNode => {
    const router = useRouter()
    const live = useLive()
    const now = useNow(1000)

    if (!session) {
        return (
            <span className='flex items-center gap-2 text-xs text-muted-foreground'>
                <span className='size-2 rounded-full bg-flat' aria-hidden='true' />
                Market data offline
            </span>
        )
    }

    const phase = live.session ? live.phase : session.phase
    const open = phase === 'open'
    const preOpen = phase === 'pre-open'

    return (
        <div className='flex min-w-0 items-center gap-3 text-xs'>
            <span className='flex items-center gap-2 whitespace-nowrap font-medium text-foreground'>
                <span
                    aria-hidden='true'
                    className={cn(
                        'size-2 rounded-full',
                        open && 'live-dot bg-up',
                        preOpen && 'bg-primary',
                        !open && !preOpen && 'bg-flat'
                    )}
                />
                {phaseLabel(phase)}
            </span>

            {!compact && (
                <span className='hidden whitespace-nowrap tabular-nums text-muted-foreground sm:inline' suppressHydrationWarning>
                    {now !== null ? `${formatEatClock(now)} EAT` : 'Nairobi, EAT'}
                </span>
            )}

            {!open && !compact && (
                <span className='hidden whitespace-nowrap text-muted-foreground md:inline'>
                    {formatNextOpen(session)}
                </span>
            )}

            {live.stale && (
                <button
                    type='button'
                    onClick={() => router.replace(router.asPath)}
                    className='inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2 py-0.5 font-medium text-primary hover:bg-primary/20'
                >
                    <RefreshCw aria-hidden='true' className='size-3' />
                    New session, refresh
                </button>
            )}
        </div>
    )
}

export default MarketStatus
